import {Component, EventEmitter, Input, OnDestroy, OnInit, Output} from '@angular/core';
import {DesignSystemModule} from "../../../../design-system/design-system.module";
import {CommonModule} from "@angular/common";
import {SnackbarModule} from "../../../../global/snackbar/snackbar.module";
import {TabModule} from "../../../../global/tabs/tab/tab.module";
import {TabsModule} from "../../../../global/tabs/tabs.module";
import {TranslateModule, TranslateService} from "@ngx-translate/core";
import {NetworkOverviewTabComponent} from "./network-overview-tab/network-overview-tab.component";
import {Network, NetworkService} from "../../../../../services/back/network.service";
import {Subscription} from "rxjs";
import {NetworkDetailData, NetworkDetailService} from "../../../../../services/front/network-detail.service";
import {NetworkUrlService} from "../../../../../services/front/network-url.service";
import {ActivatedRoute, Router} from "@angular/router";
import {SnackbarService} from "../../../../../services/front/snackbar.service";
import {MatDialog} from "@angular/material/dialog";
import {filter, tap} from "rxjs/operators";
import {DeleteDialogComponent} from "../../../../design-system/delete-dialog/delete-dialog.component";
import {NetworkSettingsComponent} from "./network-settings/network-settings.component";
import {NetworkArchitectureTabComponent} from "./network-architecture-tab/network-architecture-tab.component";
import {TitleContentLoaderComponent} from "../../../../global/title-content-loader/title-content-loader.component";

@Component({
	selector: 'app-network-detail',
	standalone: true,
	imports: [
		CommonModule,
		DesignSystemModule,
		SnackbarModule,
		TabModule,
		TabsModule,
		TranslateModule,
		NetworkOverviewTabComponent,
		NetworkSettingsComponent,
		NetworkArchitectureTabComponent,
		TitleContentLoaderComponent
	],
	templateUrl: './network-detail.component.html',
	styleUrl: './network-detail.component.scss'
})
export class NetworkDetailComponent implements OnInit, OnDestroy {
	@Input() data: NetworkDetailInput;

	@Output() onClose = new EventEmitter<void>();

	tenantId: string;
	isEditor: boolean;

	network: Network;

	_initializing: boolean;

	sliderIndex: number = 1;
	lazyLoadedTabs: boolean[] = [false, false];

	settings: boolean = false;

	subscriptions: Subscription = new Subscription();

	constructor(private networkDetailService: NetworkDetailService,
				private networkUrlService: NetworkUrlService,
				private activatedRoute: ActivatedRoute,
				private translate: TranslateService,
				private networkService: NetworkService,
				private snackbar: SnackbarService,
				private dialog: MatDialog,
				private router: Router) {
	}

	ngOnInit() {
		this.router.navigate([], {queryParams: {networkId: this.data.networkId}, queryParamsHandling: 'merge'});

		this.sliderIndex = this.networkUrlService.getCurrentNetworkTab();
		this.lazyLoadedTabs[this.sliderIndex] = true;

		this.networkDetailService.initialize(this.data.networkId);

		this.subscriptions.add(this.networkDetailService.getInitializingChanges()
			.subscribe(initializing => this._initializing = initializing));

		this.subscriptions.add(this.networkDetailService.getNetworkDetailDataChanges()
			.pipe(tap(data => this.setNetworkDetailData(data)))
			.subscribe());

		this.subscriptions.add(this.activatedRoute.queryParams
			.subscribe(() => this.onQueryParamsChange()));
	}

	private setNetworkDetailData(data: NetworkDetailData): void {
		this.tenantId = data.tenantId;
		this.isEditor = data.isEditor;
		this.network = data.network;
	}

	private onQueryParamsChange(): void {
		this.settings = this.networkUrlService.getParameter();
	}

	openDeleteNetworkPopup() {
		this.dialog.open(DeleteDialogComponent, {
			width: '475px',
			position: {
				right: '162.5px'
			},
			data: {
				icon: '/assets/illustrations/delete/network.svg',
				title: this.translate.instant('page.infrastructure.network.delete.title'),
				subtitle: this.translate.instant('page.infrastructure.network.delete.subtitle'),
				warningMessage: this.translate.instant('page.infrastructure.network.delete.warningMessage'),
				deleteButton: this.translate.instant('page.infrastructure.network.delete.deleteButton')
			}
		})
			.afterClosed()
			.pipe(
				filter(result => result)
			).subscribe(() => this.deleteNetwork());
	}

	close(): void {
		this.onClose.emit();
	}

	deleteNetwork() {
		this.networkService.delete(this.tenantId, this.network.id)
			.subscribe(() => this.onClose.emit());
	}

	onOpenAppSettings(): void {
		this.networkUrlService.navigateInAndOutOfParameter(!this.settings);
	}

	onSaveSettings(): void {
		this.snackbar.show(this.translate.instant('page.application.detail.update.success'));
		this.networkUrlService.navigateInAndOutOfParameter(false);
	}

	onSliderChange(index: number): void {
		this.sliderIndex = index;
		this.lazyLoadedTabs[index] = true;
		this.networkUrlService.changeCurrentNetworkTab(index);
	}

	ngOnDestroy() {
		this.networkDetailService.clearNetworkDetailData();
		this.subscriptions.unsubscribe();
	}
}

export interface NetworkDetailInput {
	networkId: string;
}
